import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

// ─── Shift Dashboard (z8nrz7fbcj / shell z8nrz7fbcm) ─────────────────────────
// Named views are SHARED settings, one row per view in the tenant — not a
// personal layout in UserPreference. Reads need shift_dashboard.view (or
// .manage); every write needs shift_dashboard.manage.

/** One tile on a view, in display order. Same shape as a dashboard's WidgetPlacement. */
export interface ShiftViewWidget {
  id: string
  size: string
  view?: string
}

export interface ShiftDashboardView {
  id: string
  name: string
  /** A Building. */
  siteId: string | null
  /** An Equipment of purpose ROOM. */
  roomId: string | null
  shiftDefinitionId: string | null
  teamId: string | null
  /** An AssignmentGroup. */
  groupId: string | null
  widgets: ShiftViewWidget[]
  createdBy: string
  updatedBy: string
  createdAt: string
  updatedAt: string
}

export interface ShiftViewPayload {
  name: string
  siteId?: string | null
  roomId?: string | null
  shiftDefinitionId?: string | null
  teamId?: string | null
  groupId?: string | null
  widgets?: ShiftViewWidget[]
}

/**
 * What the filter bar offers. One endpoint behind the shift-dashboard key, so a
 * viewer needs no Resource Center, Teams or Cleaning permission to fill it.
 */
export interface ShiftDashboardFilterOptions {
  sites: { id: string; code: string; name: string }[]
  /** `siteId` is the building of the floor the room is mapped to; null when unmapped. */
  rooms: { id: string; label: string; siteId: string | null }[]
  shifts: { id: string; code: string; name: string; startTime: string; endTime: string; active: boolean }[]
  teams: { id: string; name: string; groupIds: string[] }[]
  groups: { id: string; name: string }[]
}

// ─── The board (z8nrz7fbcn / fbcp / fbcr) ──────────────────────────────────
// Every count on a tile is the length of the rows its drill-down returns for
// the same query: the server computes both with one function, so they cannot
// disagree.

export type ShiftStatus = 'critical' | 'attention' | 'ready' | 'info'

export interface ShiftBoardQuery {
  date: string
  shiftDefinitionId?: string | null
  siteId?: string | null
  roomId?: string | null
  teamId?: string | null
  groupId?: string | null
}

export interface ShiftWindow {
  date: string
  /** ISO instants; the end is exclusive. */
  from: string
  to: string
  /** "Day 07:00–15:00, Wed 15 Oct 2026". */
  label: string
  shift: { id: string; code: string; name: string } | null
}

export interface ShiftTileSummary {
  id: string
  title: string
  count: number
  statusLine: string
  status: ShiftStatus
  /** False when the tile cannot be computed for this caller or tenant; `statusLine` says why. */
  available: boolean
}

export type ShiftCell =
  | string
  | number
  | null
  | { text: string; tone?: ShiftStatus | 'muted'; href?: string }

export interface ShiftRow {
  id: string
  cells: Record<string, ShiftCell>
  status: ShiftStatus
  /** The quick-filter chips this row is counted under. */
  facets: string[]
  href?: string
  detail: {
    title: string
    subtitle?: string
    badges?: { label: string; tone: ShiftStatus }[]
    fields: { label: string; value: string }[]
  }
}

export interface ShiftTileDetail {
  id: string
  title: string
  columns: { key: string; label: string; align?: 'left' | 'right' }[]
  facets: { key: string; label: string; count: number }[]
  rows: ShiftRow[]
  /** True when more rows matched than were returned. */
  truncated: boolean
  /** A filter that does not apply to this tile, e.g. "The site and room filters do not apply to stock." */
  note?: string
}

export interface ShiftAttentionItem {
  id: string
  tileId: string
  severity: ShiftStatus
  /** "Blocked, overdue". */
  badge: string
  title: string
  subtitle: string
  /** Null is shown as Unassigned. */
  owner: string | null
  href?: string
}

export interface ShiftBoard {
  window: ShiftWindow
  tiles: ShiftTileSummary[]
  attention: ShiftAttentionItem[]
  attentionTotal: number
}

export type ShiftNoteCategory = 'safety' | 'lab' | 'equipment' | 'staffing' | 'general'

export interface ShiftNote {
  id: string
  date: string
  shiftDefinitionId: string | null
  siteId: string | null
  teamId: string | null
  category: ShiftNoteCategory
  text: string
  resolved: boolean
  resolvedBy: string | null
  resolvedAt: string | null
  createdBy: string
  createdAt: string
}

export interface ShiftNotePayload {
  date: string
  shiftDefinitionId?: string | null
  siteId?: string | null
  teamId?: string | null
  category: ShiftNoteCategory
  text: string
}

/** The query string for a board-scoped GET; empty filters are left out. */
export function boardQueryString(q: ShiftBoardQuery): string {
  const p = new URLSearchParams({ date: q.date })
  const add = (k: keyof ShiftBoardQuery) => {
    const v = q[k]
    if (v) p.set(k, v)
  }
  add('shiftDefinitionId')
  add('siteId')
  add('roomId')
  add('teamId')
  add('groupId')
  return p.toString()
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const send = <T>(method: 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown) =>
  request<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) })

export const shiftDashboardApi = {
  filterOptions: () => request<ShiftDashboardFilterOptions>('/shift-dashboard/filters'),
  listViews: () => request<ShiftDashboardView[]>('/shift-dashboard/views'),
  createView: (body: ShiftViewPayload) => send<ShiftDashboardView>('POST', '/shift-dashboard/views', body),
  updateView: (id: string, body: Partial<ShiftViewPayload>) =>
    send<ShiftDashboardView>('PATCH', `/shift-dashboard/views/${id}`, body),
  removeView: (id: string) => send<{ id: string }>('DELETE', `/shift-dashboard/views/${id}`),

  board: (q: ShiftBoardQuery) => request<ShiftBoard>(`/shift-dashboard/board?${boardQueryString(q)}`),
  tile: (tileId: string, q: ShiftBoardQuery) =>
    request<ShiftTileDetail>(`/shift-dashboard/tiles/${encodeURIComponent(tileId)}?${boardQueryString(q)}`),

  notes: (q: Pick<ShiftBoardQuery, 'date' | 'shiftDefinitionId' | 'siteId' | 'teamId'>) =>
    request<ShiftNote[]>(`/shift-dashboard/notes?${boardQueryString(q)}`),
  createNote: (body: ShiftNotePayload) => send<ShiftNote>('POST', '/shift-dashboard/notes', body),
  setNoteResolved: (id: string, resolved: boolean) =>
    send<ShiftNote>('PATCH', `/shift-dashboard/notes/${id}`, { resolved }),
}
